import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Stack, Typography, Skeleton, Grid, Tooltip, useTheme, alpha,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import {
  TrendingUp, EventSeat, Payments, SentimentDissatisfied, Timer,
  AssignmentOutlined, FactCheckOutlined, CardGiftcardOutlined, HowToRegOutlined, SchoolOutlined,
  AccountBalanceWalletOutlined, HourglassEmpty, Replay,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import {
  admissionCycleService, AdmissionReports, FunnelReport, SeatUtilizationReport,
  RevenueReport, LostReasonsReport, TimeToDecisionReport, FeeBucket,
} from '../../services/admissions/admissionCycleService';
import { AdmissionCycle, ApplicationStatus } from '../../types/admissions';
import { statusLabel } from '../common/StatusChip';
import EmptyState from '../common/EmptyState';
import KpiTile from '../reports/finance/KpiTile';
import { formatCount, formatMoneyFull } from '../reports/finance/chartConfig';
import { useAuth } from '../../context/AuthContext';
import { fmtClassName } from '../../utils/pdf/format';
import dateTimeUtils from '../../utils/dateTimeUtils';
import { TabToolbar, PanelCard, TableCard, GroupLabel, tableHeadCellSx } from './tabLayout';

/**
 * ReportsTab — operator-facing analytics for a cycle. Five panels:
 *   • Funnel: conversion steps + per-status breakdown
 *   • Seat utilisation: one stacked bar per class (enrolled / accepting / offered / waitlisted / remaining)
 *   • Revenue: collected / outstanding / refunded + per-fee-kind table
 *   • Lost reasons: top withdraw / decline / rejection reasons
 *   • Time to decision: median / p75 / p90 / average days from Submitted to a decision
 */

interface Props { cycle: AdmissionCycle; }

const numSx = { fontVariantNumeric: 'tabular-nums' } as const;

const ReportsTab: React.FC<Props> = ({ cycle }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<AdmissionReports | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await admissionCycleService.reports(cycle._id);
      setData(r);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to load reports', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [cycle._id, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <TabToolbar
        summary={data ? `Generated ${dateTimeUtils.formatDateForDisplay(data.generatedAt)}` : undefined}
        onRefresh={load}
        refreshing={loading}
      />

      <Box sx={{ px: 2.5, py: 2.5 }}>
        {loading && !data ? (
          <Grid container spacing={2.5}>
            <Grid size={12}><Skeleton variant="rounded" height={220} sx={{ borderRadius: 2.5 }} /></Grid>
            <Grid size={{ xs: 12, lg: 6 }}><Skeleton variant="rounded" height={260} sx={{ borderRadius: 2.5 }} /></Grid>
            <Grid size={{ xs: 12, lg: 6 }}><Skeleton variant="rounded" height={260} sx={{ borderRadius: 2.5 }} /></Grid>
          </Grid>
        ) : !data ? (
          <EmptyState
            icon={<TrendingUp />}
            title="No reports available"
            description="Reports populate once a cycle has applications flowing through the pipeline."
          />
        ) : (
          <Grid container spacing={2.5}>
            <Grid size={12}>
              <PanelCard title="Funnel" subtitle="How applications convert through the pipeline" Icon={TrendingUp}>
                <FunnelSection data={data.funnel} />
              </PanelCard>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <PanelCard title="Seat utilisation" subtitle="Seats filled per class" Icon={EventSeat}>
                <SeatUtilizationSection data={data.seatUtilization} />
              </PanelCard>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <PanelCard title="Revenue" subtitle="Application and admission fees" Icon={Payments}>
                <RevenueSection data={data.revenue} />
              </PanelCard>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <PanelCard title="Lost reasons" subtitle="Why applicants withdrew, declined or were rejected" Icon={SentimentDissatisfied}>
                <LostReasonsSection data={data.lostReasons} />
              </PanelCard>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <PanelCard
                title="Time to decision"
                subtitle={data.timeToDecision.decidedCount > 0
                  ? `Days from submission to a decision, across ${formatCount(data.timeToDecision.decidedCount)} decided applications`
                  : 'Days from submission to a decision'}
                Icon={Timer}
              >
                <TimeToDecisionSection data={data.timeToDecision} />
              </PanelCard>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

// ─── Shared meter row ────────────────────────────────────────────────────────

/** Labelled horizontal bar: label left, count (+ share) right, thin bar below. */
const MeterRow: React.FC<{ label: string; count: number; share?: number; value: number; color: string }> = ({
  label, count, share, value, color,
}) => (
  <Box>
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2} sx={{ mb: 0.5 }}>
      <Typography variant="body2" sx={{ minWidth: 0 }} noWrap>{label}</Typography>
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexShrink: 0 }}>
        <Typography variant="body2" fontWeight={600} sx={numSx}>{formatCount(count)}</Typography>
        {share != null && (
          <Typography variant="caption" color="text.secondary" sx={{ ...numSx, minWidth: 40, textAlign: 'right' }}>
            {share.toFixed(1)}%
          </Typography>
        )}
      </Stack>
    </Stack>
    <Box sx={{ height: 6, borderRadius: 3, bgcolor: (t) => alpha(t.palette.text.primary, 0.06), overflow: 'hidden' }}>
      <Box sx={{ height: '100%', width: `${Math.min(100, value)}%`, borderRadius: 3, bgcolor: color }} />
    </Box>
  </Box>
);

// ─── Funnel ──────────────────────────────────────────────────────────────────

const FUNNEL_GROUPS: Array<{ label: string; keys: ApplicationStatus[] }> = [
  { label: 'In progress', keys: ['Draft', 'Submitted', 'AppFeePending', 'UnderReview', 'DocsRequired', 'AssessmentScheduled', 'Absent', 'Assessed'] },
  { label: 'Decision',    keys: ['Offered', 'Waitlisted', 'Rejected', 'Ineligible'] },
  { label: 'Accepting',   keys: ['OfferAccepted', 'ConfirmedConditional', 'AdmFeePending', 'Confirmed'] },
  { label: 'Final',       keys: ['Enrolled', 'OfferDeclined', 'OfferLapsed', 'NotAdmitted', 'Withdrawn'] },
];

const FunnelSection: React.FC<{ data: FunnelReport }> = ({ data }) => {
  const theme = useTheme();
  if (data.total === 0) {
    return (
      <EmptyState
        dense
        icon={<AssignmentOutlined />}
        title="No applications yet"
        description="Conversion appears here as applications move through the pipeline."
      />
    );
  }
  const max = Math.max(...Object.values(data.byStatus));
  const pctSub = 'of all applications';

  const steps: Array<React.ComponentProps<typeof KpiTile>> = [
    { label: 'Total',    value: formatCount(data.total),          sub: 'applications received', icon: <AssignmentOutlined />, tone: 'primary' },
    { label: 'Assessed', value: `${data.conversion.assessedPct}%`, sub: pctSub, icon: <FactCheckOutlined /> },
    { label: 'Offered',  value: `${data.conversion.offeredPct}%`,  sub: pctSub, icon: <CardGiftcardOutlined /> },
    { label: 'Accepted', value: `${data.conversion.acceptedPct}%`, sub: pctSub, icon: <HowToRegOutlined /> },
    { label: 'Enrolled', value: `${data.conversion.enrolledPct}%`, sub: pctSub, icon: <SchoolOutlined />, tone: 'success' },
  ];

  const groups = FUNNEL_GROUPS
    .map(g => ({ ...g, keys: g.keys.filter(k => (data.byStatus[k] || 0) > 0) }))
    .filter(g => g.keys.length > 0);

  return (
    <Stack spacing={3}>
      <Grid container spacing={1.5}>
        {steps.map(s => (
          <Grid key={s.label} size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
            <KpiTile {...s} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {groups.map(g => (
          <Grid key={g.label} size={{ xs: 12, md: 6, xl: 3 }}>
            <GroupLabel count={g.keys.reduce((s, k) => s + (data.byStatus[k] || 0), 0)}>{g.label}</GroupLabel>
            <Stack spacing={1.5}>
              {g.keys.map(k => {
                const count = data.byStatus[k] || 0;
                return (
                  <MeterRow
                    key={k}
                    label={statusLabel({ kind: 'application', value: k })}
                    count={count}
                    share={(count / data.total) * 100}
                    value={max > 0 ? (count / max) * 100 : 0}
                    color={alpha(theme.palette.primary.main, 0.7)}
                  />
                );
              })}
            </Stack>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
};

// ─── Seat utilisation ────────────────────────────────────────────────────────

type SeatKey = 'enrolled' | 'accepting' | 'offered' | 'waitlisted';

const SeatUtilizationSection: React.FC<{ data: SeatUtilizationReport[] }> = ({ data }) => {
  const theme = useTheme();
  if (data.length === 0) {
    return (
      <EmptyState
        dense
        icon={<EventSeat />}
        title="No seat matrix"
        description="Configure seats per class in the cycle settings to track utilisation."
      />
    );
  }

  const segments: Array<{ key: SeatKey; label: string; color: string }> = [
    { key: 'enrolled',   label: 'Enrolled',   color: theme.palette.success.main },
    { key: 'accepting',  label: 'Accepting',  color: alpha(theme.palette.success.main, 0.45) },
    { key: 'offered',    label: 'Offered',    color: theme.palette.info.main },
    { key: 'waitlisted', label: 'Waitlisted', color: theme.palette.warning.main },
  ];
  const remainingColor = alpha(theme.palette.text.primary, 0.06);

  return (
    <Stack spacing={2.5}>
      {/* One legend for every bar */}
      <Stack direction="row" useFlexGap flexWrap="wrap" sx={{ columnGap: 2, rowGap: 0.75 }}>
        {[...segments, { key: 'remaining', label: 'Remaining', color: remainingColor }].map(s => (
          <Stack key={s.key} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: 0.75, bgcolor: s.color, border: s.key === 'remaining' ? 1 : 0, borderColor: 'divider' }} />
            <Typography variant="caption" color="text.secondary">{s.label}</Typography>
          </Stack>
        ))}
      </Stack>

      {data.map(row => {
        const filled = segments.reduce((sum, s) => sum + row[s.key], 0);
        const total = Math.max(row.hardCap, filled);
        const breakdown = [
          ...segments.filter(s => row[s.key] > 0).map(s => `${formatCount(row[s.key])} ${s.label.toLowerCase()}`),
          `${formatCount(row.remaining)} remaining`,
        ].join(' · ');
        return (
          <Box key={row.classId}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2} sx={{ mb: 0.75 }}>
              <Typography variant="body2" fontWeight={600} noWrap>{fmtClassName(row.className) || 'Class'}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ...numSx, flexShrink: 0 }}>
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{formatCount(filled)}</Box>
                {' '}of {formatCount(row.hardCap)} seats
              </Typography>
            </Stack>
            <Box sx={{ height: 12, borderRadius: 1.5, display: 'flex', overflow: 'hidden', bgcolor: remainingColor }}>
              {segments.map(s => (
                row[s.key] > 0 && total > 0 ? (
                  <Tooltip key={s.key} title={`${s.label}: ${formatCount(row[s.key])}`} arrow>
                    <Box sx={{ width: `${(row[s.key] / total) * 100}%`, bgcolor: s.color }} />
                  </Tooltip>
                ) : null
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, ...numSx }}>
              {breakdown}
              <Box component="span" sx={{ color: 'text.disabled' }}>
                {`  ·  ${formatCount(row.totalSeats)} base + ${row.overbookPercent}% overbook`}
              </Box>
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
};

// ─── Revenue ─────────────────────────────────────────────────────────────────

const RevenueSection: React.FC<{ data: RevenueReport }> = ({ data }) => {
  const { currency } = useAuth();
  const noMoney = data.applicationFees.invoiceCount === 0 && data.admissionFees.invoiceCount === 0;
  if (noMoney) {
    return (
      <EmptyState
        dense
        icon={<Payments />}
        title="No fee activity yet"
        description="Application and admission fee invoices for this cycle will be summarised here."
      />
    );
  }
  const money = (n: number) => formatMoneyFull(n, currency);
  const tiles: Array<React.ComponentProps<typeof KpiTile>> = [
    { label: 'Collected',   value: money(data.totalCollected),   icon: <AccountBalanceWalletOutlined />, tone: 'success' },
    { label: 'Outstanding', value: money(data.totalOutstanding), icon: <HourglassEmpty />, tone: data.totalOutstanding > 0 ? 'warning' : 'neutral' },
    { label: 'Refunded',    value: money(data.totalRefunded),    icon: <Replay />, tone: data.totalRefunded > 0 ? 'info' : 'neutral' },
  ];
  const buckets: Array<{ label: string; bucket: FeeBucket }> = [
    { label: 'Application fee', bucket: data.applicationFees },
    { label: 'Admission fee',   bucket: data.admissionFees },
  ];

  return (
    <Stack spacing={2.5}>
      <Grid container spacing={1.5}>
        {tiles.map(t => (
          <Grid key={t.label} size={{ xs: 12, sm: 4 }}>
            <KpiTile {...t} />
          </Grid>
        ))}
      </Grid>

      <TableCard footer="Collected is the sum of paid amounts across both fees. Outstanding excludes any refunded portion.">
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadCellSx}>Fee</TableCell>
                <TableCell sx={tableHeadCellSx} align="right">Invoices</TableCell>
                <TableCell sx={tableHeadCellSx} align="right">Billed</TableCell>
                <TableCell sx={tableHeadCellSx} align="right">Paid</TableCell>
                <TableCell sx={tableHeadCellSx} align="right">Outstanding</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {buckets.map(({ label, bucket }) => (
                <TableRow key={label} sx={{ '&:last-of-type td': { borderBottom: 0 } }}>
                  <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</TableCell>
                  <TableCell align="right" sx={numSx}>{formatCount(bucket.invoiceCount)}</TableCell>
                  <TableCell align="right" sx={{ ...numSx, whiteSpace: 'nowrap' }}>{money(bucket.billed)}</TableCell>
                  <TableCell align="right" sx={{ ...numSx, whiteSpace: 'nowrap' }}>{money(bucket.paid)}</TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...numSx, whiteSpace: 'nowrap', color: bucket.outstanding > 0 ? 'warning.dark' : 'text.secondary' }}
                  >
                    {money(bucket.outstanding)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </TableCard>
    </Stack>
  );
};

// ─── Lost reasons ────────────────────────────────────────────────────────────

const LostReasonsSection: React.FC<{ data: LostReasonsReport }> = ({ data }) => {
  const theme = useTheme();
  const lists = [
    { title: 'Withdrawn',      items: data.withdrawn, color: theme.palette.warning.main },
    { title: 'Offer declined', items: data.declined,  color: theme.palette.text.secondary },
    { title: 'Rejected',       items: data.rejected,  color: theme.palette.error.main },
  ].filter(l => l.items.length > 0);

  if (lists.length === 0) {
    return (
      <EmptyState
        dense
        icon={<SentimentDissatisfied />}
        title="Nothing lost yet"
        description="Withdrawals, declined offers and rejections will be grouped by reason here."
      />
    );
  }

  return (
    <Stack spacing={2.5}>
      {lists.map(l => {
        const total = l.items.reduce((s, x) => s + x.count, 0);
        return (
          <Box key={l.title}>
            <GroupLabel count={total}>{l.title}</GroupLabel>
            <Stack spacing={1.5}>
              {l.items.map((it, i) => {
                const share = total > 0 ? (it.count / total) * 100 : 0;
                return (
                  <MeterRow
                    key={`${l.title}-${i}`}
                    label={it.reason ? it.reason.charAt(0).toUpperCase() + it.reason.slice(1) : 'No reason given'}
                    count={it.count}
                    share={share}
                    value={share}
                    color={alpha(l.color, 0.7)}
                  />
                );
              })}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

// ─── Time to decision ────────────────────────────────────────────────────────

const TimeToDecisionSection: React.FC<{ data: TimeToDecisionReport }> = ({ data }) => {
  if (data.decidedCount === 0) {
    return (
      <EmptyState
        dense
        icon={<Timer />}
        title="No decisions yet"
        description="Turnaround times appear once applications reach a decision."
      />
    );
  }
  const days = (v: number | null) => v == null ? '—' : `${v} ${v === 1 ? 'day' : 'days'}`;
  const tiles: Array<React.ComponentProps<typeof KpiTile>> = [
    { label: 'Median',  value: days(data.medianDays), sub: 'Half decided within', tone: 'success' },
    { label: 'Average', value: days(data.avgDays),    sub: 'Mean turnaround' },
    { label: 'p75',     value: days(data.p75Days),    sub: '75% decided within' },
    { label: 'p90',     value: days(data.p90Days),    sub: '90% decided within', tone: 'warning' },
  ];
  return (
    <Grid container spacing={1.5}>
      {tiles.map(t => (
        <Grid key={t.label} size={{ xs: 12, sm: 6 }}>
          <KpiTile {...t} />
        </Grid>
      ))}
    </Grid>
  );
};

export default ReportsTab;
